import { createContext, useContext, useState } from 'react'
import { Dispatch, SetStateAction } from 'react'

import { useActiveMerchantForRecord } from 'lib/merchant-group'

import { NewExpenseSchema } from 'components/Modal/ModalNewExpense/schema'

export type ExpenseFormModalState = ExpenseModalProps & {
  isRecurrence?: boolean
  defaultValues?: Partial<NewExpenseSchema>
  // SC-10999: duplicação vinda de um detalhe. O formulário não tem uuid (cria
  // uma conta nova), mas na visão multi-empresas fica na empresa da original,
  // dona do fornecedor, da categoria e da conta copiados.
  sourceUuid?: string
}

export interface AccountsPayableState {
  expenseFormModal: ExpenseFormModalState
  setExpenseFormModal: Dispatch<SetStateAction<ExpenseFormModalState>>

  reloadTable: boolean
  setReloadTable: Dispatch<SetStateAction<boolean>>

  expenseDetailModal: ExpenseModalProps & { isRecurrence?: boolean }
  setExpenseDetailModal: Dispatch<
    SetStateAction<ExpenseModalProps> & { isRecurrence?: boolean }
  >

  expenseMarkAsPaidModal: ExpenseModalProps & {
    amount?: string
    dueDate?: string
  }
  setExpenseMarkAsPaidModal: Dispatch<
    SetStateAction<ExpenseModalProps & { amount?: string; dueDate?: string }>
  >

  expenseModalTable: ExpenseCalendarModalProps
  setExpenseModalTable: Dispatch<SetStateAction<ExpenseCalendarModalProps>>

  contentType: ContentType
  setContentType: Dispatch<SetStateAction<ContentType>>

  expensesContentType: ExpensesContentType
  setExpensesContentType: Dispatch<SetStateAction<ExpensesContentType>>

  expensesDashboardType: ExpensesDashboardType
  setExpensesDashboardType: Dispatch<SetStateAction<ExpensesDashboardType>>
  pieDashboardType: ExpensesDashboardType
  setPieDashboardType: Dispatch<SetStateAction<ExpensesDashboardType>>
  configurationDrawer: ConfigurationDrawerProps
  setConfigurationDrawer: Dispatch<SetStateAction<ConfigurationDrawerProps>>
}

export interface AccountsPayableProps {
  children: React.ReactNode
}

export interface ExpenseModalProps {
  isOpen: boolean
  uuid?: string
  isRecurrence?: boolean
}

export interface ExpenseCalendarModalProps {
  isOpen: boolean
  date?: string
}

export interface ConfigurationDrawerProps {
  isOpen: boolean
  payeeUuid?: string | undefined
}

export type ContentType = 'expenses' | 'dashboard'
export type ExpensesContentType = 'list' | 'calendar'
export type ExpensesDashboardType = 'originalAmount' | 'amountPaid'
export const AccountsPayableContext = createContext({} as AccountsPayableState)

export function AccountsPayableProvider({ children }: AccountsPayableProps) {
  const [expenseFormModal, setExpenseFormModal] =
    useState<ExpenseFormModalState>({
      isOpen: false,
      uuid: '',
      isRecurrence: false
    })

  const [reloadTable, setReloadTable] = useState<boolean>(false)
  const [expenseDetailModal, setExpenseDetailModal] = useState<
    ExpenseModalProps & { isRecurrence?: boolean }
  >({
    isOpen: false,
    uuid: '',
    isRecurrence: false
  })

  const [expenseMarkAsPaidModal, setExpenseMarkAsPaidModal] = useState<
    ExpenseModalProps & { amount?: string; dueDate?: string }
  >({ isOpen: false, uuid: '', amount: '', dueDate: '' })
  const [configurationDrawer, setConfigurationDrawer] =
    useState<ConfigurationDrawerProps>({ isOpen: false })
  const [expenseModalTable, setExpenseModalTable] =
    useState<ExpenseCalendarModalProps>({ isOpen: false, date: '' })
  const [contentType, setContentType] = useState<ContentType>('expenses')
  const [expensesContentType, setExpensesContentType] =
    useState<ExpensesContentType>('list')
  const [expensesDashboardType, setExpensesDashboardType] =
    useState<ExpensesDashboardType>('originalAmount')

  const [pieDashboardType, setPieDashboardType] =
    useState<ExpensesDashboardType>('originalAmount')

  // SC-10999: aberto da lista agregada do grupo, o detalhe (e o editar dele) e
  // o "marcar como paga" agem na empresa da conta.
  useActiveMerchantForRecord(
    expenseDetailModal.isOpen ? expenseDetailModal.uuid : undefined
  )
  useActiveMerchantForRecord(
    expenseMarkAsPaidModal.isOpen ? expenseMarkAsPaidModal.uuid : undefined
  )
  useActiveMerchantForRecord(
    expenseFormModal.isOpen ? expenseFormModal.uuid : undefined
  )

  return (
    <AccountsPayableContext.Provider
      value={{
        expenseFormModal,
        setExpenseFormModal,
        contentType,
        setContentType,
        expensesContentType,
        setExpensesContentType,
        configurationDrawer,
        setConfigurationDrawer,
        expenseDetailModal,
        setExpenseDetailModal,
        expenseMarkAsPaidModal,
        setExpenseMarkAsPaidModal,
        expensesDashboardType,
        setExpensesDashboardType,
        expenseModalTable,
        setExpenseModalTable,
        pieDashboardType,
        setPieDashboardType,
        setReloadTable,
        reloadTable
      }}
    >
      {children}
    </AccountsPayableContext.Provider>
  )
}

export const useAccountsPayableContext = (): AccountsPayableState => {
  const context = useContext(AccountsPayableContext)

  if (!context) {
    throw new Error(
      'useAccountsPayableContext should be used within an AccountsPayableContext'
    )
  }

  return context as AccountsPayableState
}
